/**
 * BadgeSystem - Sistema de conquistas e badges visuais
 */
import React from 'react';
import { Award, Star, Zap, Trophy, Heart, Target, Book, Users, Flame, Crown } from 'lucide-react';

// Definição de badges disponíveis
const BADGES = {
  // Badges de Ilhas
  ilha_1: {
    id: 'ilha_1',
    name: 'Primeira Ilha',
    description: 'Complete sua primeira ilha',
    icon: Star,
    color: '#10B981',
    requirement: (progress) => progress.completed_islands >= 1,
  },
  ilha_3: {
    id: 'ilha_3',
    name: 'Meio Caminho',
    description: 'Complete 3 ilhas',
    icon: Target,
    color: '#8B5CF6',
    requirement: (progress) => progress.completed_islands >= 3,
  },
  ilha_todas: {
    id: 'ilha_todas',
    name: 'Mestre das Ilhas',
    description: 'Complete todas as 5 ilhas',
    icon: Crown,
    color: '#F59E0B',
    requirement: (progress) => progress.completed_islands >= 5,
  },
  
  // Badges de Estágios
  estagio_2: {
    id: 'estagio_2',
    name: 'Crescimento Sólido',
    description: 'Atinja estágio 2 da Acácia',
    icon: Zap,
    color: '#0F6E56',
    requirement: (progress) => progress.stage >= 2,
  },
  estagio_4: {
    id: 'estagio_4',
    name: 'Acácia Plena',
    description: 'Atinja o estágio máximo',
    icon: Trophy,
    color: '#C8541A',
    requirement: (progress) => progress.stage >= 4,
  },
  
  // Badges de Engajamento
  streak_7: {
    id: 'streak_7',
    name: 'Semana Dedicada',
    description: '7 dias consecutivos de atividade',
    icon: Flame,
    color: '#EF4444',
    requirement: (progress) => progress.streak >= 7,
  },
  streak_30: {
    id: 'streak_30',
    name: 'Mês Consistente',
    description: '30 dias consecutivos',
    icon: Flame,
    color: '#DC2626',
    requirement: (progress) => progress.streak >= 30,
  },
  
  // Badges de Lições
  licoes_10: {
    id: 'licoes_10',
    name: 'Aprendiz Dedicado',
    description: 'Complete 10 lições',
    icon: Book,
    color: '#3B82F6',
    requirement: (progress) => progress.completed_lessons >= 10,
  },
  licoes_50: {
    id: 'licoes_50',
    name: 'Estudioso',
    description: 'Complete 50 lições',
    icon: Book,
    color: '#2563EB',
    requirement: (progress) => progress.completed_lessons >= 50,
  },
  
  // Badges Especiais
  primeira_rega: {
    id: 'primeira_rega',
    name: 'Primeira Rega',
    description: 'Regue sua Acácia pela primeira vez',
    icon: Heart,
    color: '#EC4899',
    requirement: (progress) => progress.times_watered >= 1,
  },
  circulo_ativo: {
    id: 'circulo_ativo',
    name: 'Jardineiro Coletivo',
    description: 'Participe ativamente do círculo',
    icon: Users,
    color: '#14B8A6',
    requirement: (progress) => progress.circle_participation >= 5,
  },
};

// Badge Component
const Badge = ({ badge, unlocked, onClick }) => {
  const Icon = badge.icon;
  
  return (
    <div
      onClick={() => onClick && onClick(badge)}
      className={`
        relative group cursor-pointer transition-all transform hover:scale-110
        ${unlocked ? '' : 'opacity-40 grayscale'}
      `}
    >
      <div
        className={`
          w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg
          ${unlocked ? 'shadow-xl' : 'shadow-md'}
        `}
        style={{
          background: unlocked
            ? `linear-gradient(135deg, ${badge.color}, ${badge.color}CC)`
            : '#E5E7EB',
        }}
      >
        <Icon className="w-10 h-10 text-white" />
      </div>
      
      {/* Unlock indicator */}
      {unlocked && (
        <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
          <Award className="w-4 h-4 text-white" />
        </div>
      )}
      
      {/* Tooltip on hover */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
        <div className="font-bold mb-1">{badge.name}</div>
        <div className="text-gray-300">{badge.description}</div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  );
};

// Main BadgeSystem Component
export default function BadgeSystem({ userProgress, onBadgeClick }) {
  // Calculate which badges are unlocked
  const badgeList = Object.values(BADGES);
  const unlockedBadges = badgeList.filter(badge => badge.requirement(userProgress));
  const lockedBadges = badgeList.filter(badge => !badge.requirement(userProgress));
  
  const unlockedCount = unlockedBadges.length;
  const totalCount = badgeList.length;
  const completionPct = Math.round((unlockedCount / totalCount) * 100);
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Conquistas</h2>
          <p className="text-sm text-gray-600">
            {unlockedCount} de {totalCount} badges desbloqueados
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-purple-600">{completionPct}%</div>
          <div className="text-xs text-gray-500">Completo</div>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mb-8">
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </div>
      
      {/* Unlocked Badges */}
      {unlockedBadges.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Desbloqueados
          </h3>
          <div className="flex flex-wrap gap-4">
            {unlockedBadges.map(badge => (
              <Badge
                key={badge.id}
                badge={badge}
                unlocked={true}
                onClick={onBadgeClick}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Locked Badges */}
      {lockedBadges.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-500 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Bloqueados
          </h3>
          <div className="flex flex-wrap gap-4">
            {lockedBadges.map(badge => (
              <Badge
                key={badge.id}
                badge={badge}
                unlocked={false}
                onClick={onBadgeClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Hook para verificar novos badges desbloqueados
export function useNewBadges(previousProgress, currentProgress) {
  const [newBadges, setNewBadges] = React.useState([]);
  
  React.useEffect(() => {
    if (!previousProgress || !currentProgress) return;
    
    const badgeList = Object.values(BADGES);
    const newlyUnlocked = badgeList.filter(badge => {
      const wasLocked = !badge.requirement(previousProgress);
      const isNowUnlocked = badge.requirement(currentProgress);
      return wasLocked && isNowUnlocked;
    });
    
    if (newlyUnlocked.length > 0) {
      setNewBadges(newlyUnlocked);
    }
  }, [previousProgress, currentProgress]);
  
  return newBadges;
}
